import {
  Box,
  Container,
  FormControl,
  Grid,
  InputLabel,
  Select,
  TextField,
  Paper,
  Button,
  MenuItem,
  FormControlLabel,
  Checkbox,
  IconButton,
  Typography,
  Tooltip,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { useDispatch, useSelector } from 'react-redux';
import KeycloakService from '../../services/KeycloakService';
import { getEmployeesInProject } from '../../services/ProjectService';
import { getReportingManagerForLeave } from '../../services/UserService';
import {
  getSecondsFromHHMMSS,
  getHHMMSSFromSeconds,
} from '../../utils/TimeConversionFunctions';
import {
  CANCEL_BUTTON_TEXT,
  ERROR_KEY,
  RESET_DATE_RANGE,
  SHOW_NOTIFICATION,
  SUBMIT_BUTTON_TEXT,
} from '../../constants';
import {
  APPRAISAL,
  APPROVER,
  countNonWhitespaceCharacters,
  PROJECT,
  TaskFieldError,
  TASK_DESCRIPTION,
  TASK_EDIT_CLOSE,
  TASK_TILE,
  WFO,
  YYYY_MM_DD,
} from './TaskConstants';

import { APPROVED, WFHStatusType } from './WFHStatusconstants';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CloseIcon from '@mui/icons-material/Close';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { addWFHTask, deleteWFHTask } from '../../services/WFHService';
import dayjs from 'dayjs';
import { getSummary } from '../../services/TimingService';
import { getEmployeeWfhQuota } from '../../admin/Services/EmployeePrivilegesService';

const WFHTask = (props) => {
  const currentUserId = KeycloakService.getUserDetail();
  const {
    selectedDates,
    userProjects,
    approvalContext,
    handleClose,
    wfh,
    refresh,
    setRefresh,
    editableWFHTasks,
    editable,
    taskData,
    officialHolidays,
    leavesList,
  } = props;

  const getEligibleManagerIdsForProject = (projectId) => {
    if (!approvalContext?.projects?.length) {
      return null;
    }
    const project = approvalContext.projects.find(
      (p) => p.projectId === projectId,
    );
    if (!project) {
      return [];
    }
    if (project.eligibleManagerIds?.length) {
      return project.eligibleManagerIds;
    }
    return project.frozenManagerIds || [];
  };

  const isProjectAndApproverDisabled = (task) =>
    task.status === 'APPROVED' || (editable && task.id != null);

  const loadApproversForProject = async (projectId) => {
    const response = await getEmployeesInProject(projectId, 'ACTIVE');
    if (response.isError) {
      return [];
    }
    const eligibleIds = getEligibleManagerIdsForProject(projectId);
    if (eligibleIds === null) {
      return response.data.content.filter((item) => item.isManager);
    }
    const approversInProject = response.data.content.filter(
      (item) =>
        item.isManager &&
        eligibleIds.includes(item.employeeId || item.id),
    );
    if (approversInProject.length > 0) {
      return approversInProject;
    }
    // Sole-manager edge case: the eligible approver (the employee's Reporting
    // Manager, routed the same way as the parent WFH request) is not a member
    // of this project, so getEmployeesInProject never returns them. Fall back
    // to resolving them directly.
    const seniorManager = await getReportingManagerForLeave(currentUserId);
    if (
      seniorManager.isError === false &&
      seniorManager.data?.data &&
      eligibleIds.includes(seniorManager.data.data.id)
    ) {
      const manager = seniorManager.data.data;
      return [
        {
          ...manager,
          employeeId: manager.id,
          employeeName: `${manager.firstName || ''} ${manager.lastName || ''}`.trim(),
          isManager: true,
        },
      ];
    }
    return [];
  };

  const [fieldError, setFieldError] = React.useState({
    project: false,
    taskType: false,
    taskTitle: false,
    timeDuration: false,
  });

  const [errorMsg, setErrorMsg] = React.useState({
    project: '',
    taskType: '',
    taskTitle: '',
    timeDuration: '',
  });
  const [tasksToBeDeleted, setTasksToBeDeleted] = useState([]);
  const startDate = selectedDates[0]?.startDate;
  const endDate = selectedDates[0]?.endDate;
  const [approverList, setApproverList] = React.useState([[]]);
  const [timeErrorMsg, setTimeErrorMsg] = React.useState('');
  const [loadingApiCall, setLoadingApiCall] = React.useState(false);
  const [areAllTitlesDetailsValid, setAreAllTitlesDetailsValid] =
    React.useState(true);
  const [multipleTasks, setMultipleTasks] = React.useState([
    {
      date: moment(new Date()).format(YYYY_MM_DD),
      timings: [
        {
          dateOfWfh: moment(new Date()).format(YYYY_MM_DD),
          title: '',
          applicationId: wfh.id,
          description: '',
          projectId: '',
          taskType: '',
          approverId: '',
          officeHours: null,
          requestedWfhHours: null,
          approvedWfhHours: null,
          isAppraisal: false,
          externalHours: 0,
          timeError: false,
          status: WFHStatusType.PENDING,
          timeErrorMsg: '',
          titleError: false,
          descriptionError: false,
          projectError: false,
          approverIdError: false,
          isUpdatedByHr: false,
        },
      ],
    },
  ]);

  const dispatch = useDispatch();
  const isWeekend = (dt) => {
    return (
      moment(dt, 'DD MM YYYY').day() === 0 ||
      moment(dt, 'DD MM YYYY').day() === 6
    );
  };
  const isHolidayOrLeave = (dt) => {
    if (
      officialHolidays.includes(dt) ||
      leavesList.includes(dt) ||
      isWeekend(dt)
    ) {
      return true;
    }
    return false;
  };
  useEffect(() => {
    const currentDate = new Date();
    const dt = new Date(startDate);
    const enddate = new Date(endDate);
    const et = currentDate <= enddate ? currentDate : endDate;
    let updatedTasks = [];
    while (dt <= et) {
      const day = dt.getDate().toString().padStart(2, '0');
      const month = (dt.getMonth() + 1).toString().padStart(2, '0');
      const year = dt.getFullYear().toString();
      if (editable === true) {
        const element = [];
        var count = 0;
        taskData.map((row) =>
          moment(row.dateOfWfh).format('YYYY-MM-DD') ===
          moment(dt).format('YYYY-MM-DD')
            ? ((element[count] = {
                dateOfWfh: row.dateOfWfh,
                id: row.id,
                employeeId: parseInt(currentUserId),
                applicationId: row.applicationId,
                title: row.title,
                description: row.description,
                projectId: row.projectId,
                approvedWfhHours: 0,
                approverId: row.approverId,
                requestedWfhHours: getHHMMSSFromSeconds(row.requestedWfhHours),
                isAppraisal: row.isAppraisal,
                status: row.status,
                externalHours: 0,
                timeError: false,
                timeErrorMsg: '',
                titleError: false,
                descriptionError: false,
                projectError: false,
                approverIdError: false,
                approverIdErrorMsg: '',
              }),
              count++)
            : null,
        );
        updatedTasks = [
          ...updatedTasks,
          ...[{ date: moment(dt).format('YYYY-MM-DD'), timings: element }],
        ];
        dt.setDate(dt.getDate() + 1);
      } else {
        const element = [
          {
            dateOfWfh: moment(dt).format(YYYY_MM_DD),
            employeeId: parseInt(currentUserId),
            applicationId: wfh.id,
            title: '',
            description: '',
            projectId: wfh.projectId,
            approvedWfhHours: null,
            approverId: wfh.approverId,
            requestedWfhHours: null,
            status: 'PENDING',
            externalHours: 0,
            timeError: false,
            timeErrorMsg: '',
            titleError: false,
            descriptionError: false,
            projectError: false,
            approverIdError: false,
            approverIdErrorMsg: '',
          },
        ];
        if (!isHolidayOrLeave(`${day} ${month} ${year}`)) {
          updatedTasks = [
            ...updatedTasks,
            ...[{ date: moment(dt).format('YYYY-MM-DD'), timings: element }],
          ];
        } else {
          updatedTasks = [
            ...updatedTasks,
            ...[{ date: moment(dt).format('YYYY-MM-DD'), timings: [] }],
          ];
        }
        dt.setDate(dt.getDate() + 1);
      }
    }
    const rows = updatedTasks.length;
    const columns = 1;
    const approverList = Array.from({ length: rows }, () =>
      Array.from({ length: columns }, () => []),
    );
    setApproverList(approverList);
    setMultipleTasks(updatedTasks);
    approverListSet(updatedTasks);
  }, []);

  const approverListSet = async (updatedTasks) => {
    if (editable) {
      for (let i = 0; i < updatedTasks.length; i++) {
        const items = updatedTasks[i];
        for (let j = 0; j < items.timings.length; j++) {
          const item = items.timings[j];
          const list = await loadApproversForProject(item.projectId);
          setApproverList((prevApproverList) => {
            const updatedList = [...prevApproverList];
            updatedList[i][j] = list;
            return updatedList;
          });
        }
      }
    }
  };

  const dipatchNotification = (typ, msg) => {
    dispatch({
      type: SHOW_NOTIFICATION,
      data: {
        open: true,
        type: typ,
        message: msg,
      },
    });
  };

  const handleChangeForRangeSelection = (e, index, timingsIndex) => {
    setMultipleTasks((prevTasks) =>
      prevTasks.map((task, i) => {
        if (i === index) {
          const newTimings = [...task.timings];
          newTimings[timingsIndex] = {
            ...newTimings[timingsIndex],
            requestedWfhHours:
              e == null
                ? moment(new Date(e).setHours(0, 0, 0, 0)).format('HH:mm')
                : moment(new Date(e)).format('HH:mm'),
          };
          return { ...task, timings: newTimings };
        }
        return task;
      }),
    );
  };
  const handleOnChangeAppraisal = (index, timingsIndex) => {
    setMultipleTasks(
      multipleTasks.map((task, i) => {
        if (i === index) {
          return {
            ...task,
            timings: task.timings.map((timing, j) => {
              if (j === timingsIndex) {
                return {
                  ...timing,
                  isAppraisal: !timing.isAppraisal,
                };
              }
              return timing;
            }),
          };
        }
        return task;
      }),
    );
  };

  const handleCancelForm = () => {
    dispatch({
      type: TASK_EDIT_CLOSE,
    });
    dispatch({
      type: RESET_DATE_RANGE,
    });

    handleClose();
  };
  const dataValidation = () => {
    for (let i = 0; i < multipleTasks.length; i++) {
      for (let j = 0; j < multipleTasks[i].timings.length; j++) {
        hourValidation(multipleTasks[i].timings[j].requestedWfhHours, i, j);
      }
    }
    const newTasks = multipleTasks.map((task) => {
      const updatedTimings = task.timings.map((timing) => {
        const hasTitle =
          typeof timing.title === 'string' &&
          countNonWhitespaceCharacters(timing.title) >= 30;
        const hasDescripton =
          typeof timing.description === 'string' &&
          countNonWhitespaceCharacters(timing.description) >= 100;
        const hasProject =
          typeof timing.projectId === 'number' && timing.projectId !== '';
        const hasApprover =
          typeof timing.approverId === 'string' && timing.approverId !== '';
        return {
          ...timing,
          titleError: !hasTitle,
          descriptionError: !hasDescripton,
          projectError: !hasProject,
          approverIdError: !hasApprover,
        };
      });
      return {
        ...task,
        timings: updatedTimings,
      };
    });
    setMultipleTasks(newTasks);
    const hasErrors = newTasks.some((task) => {
      return task.timings.some((timing) => {
        return (
          timing.timeError ||
          timing.titleError ||
          timing.projectError ||
          timing.approverIdError ||
          timing.descriptionError
        );
      });
    });

    if (hasErrors) {
      return false;
    }
    return true;
  };

  // const isMonthlyQuotaValid = async (payload) => {
  //   try {
  //     const { employeeId, wfhTaskDTOList } = payload;

  //     // Group tasks by month
  //     const tasksGroupedByMonth = wfhTaskDTOList.reduce((acc, task) => {
  //       const taskMonth = moment(task.dateOfWfh).startOf('month').format('YYYY-MM');
  //       if (!acc[taskMonth]) {
  //         acc[taskMonth] = [];
  //       }
  //       acc[taskMonth].push(task);
  //       return acc;
  //     }, {});

  //     //console.log("Grouped tasks by month:", tasksGroupedByMonth);

  //     // Iterating over grouped months
  //     for (const [month, tasks] of Object.entries(tasksGroupedByMonth)) {

  //       const startDate = moment(month, 'YYYY-MM').startOf('month').format('YYYY-MM-DD');
  //       const endDate = moment(month, 'YYYY-MM').endOf('month').format('YYYY-MM-DD');

  //       // summary data for the month
  //       const summaryData = await getSummary(startDate, endDate, employeeId);
  //       const totalUsedHours = summaryData?.data?.totalWfhHours / 1000 || 0;
  //       //console.log("Total used hours:", totalUsedHours);

  //       // Fetching monthly quota for the employee
  //       const taskMonthIndex = moment(month, 'YYYY-MM').month();
  //       const taskYear = moment(month, 'YYYY-MM').year();
  //       const quotaResponse = await getEmployeeWfhQuota(taskYear, employeeId);
  //       const monthlyQuotaHours = quotaResponse?.data?.data?.quotaWFH?.thisYear[taskMonthIndex] || 0;
  //       const monthlyQuotaMs = monthlyQuotaHours * 3600;
  //       // console.log("Monthly quota (ms):", monthlyQuotaMs);

  //       // Calculating total requested hours for the tasks in the month
  //       const requestedHours = tasks.reduce(
  //         (acc, task) => acc + (task.requestedWfhHours || 0),
  //         0
  //       );
  //       // console.log("Requested hours (ms):", requestedHours);

  //       // Validate requested hours against the remaining quota
  //       const remainingQuota = monthlyQuotaMs - totalUsedHours; // Remaining quota in ms
  //       // console.log("Remaining quota (ms):", remainingQuota);

  //       if (requestedHours > remainingQuota) {
  //         console.warn(`Quota exceeded for month ${month}:`, {
  //           totalUsedHours,
  //           requestedHours,
  //           monthlyQuotaMs,
  //         });
  //         return false;
  //       }
  //     }

  //     // If all months pass validation
  //     return true;
  //   } catch (error) {
  //     //Error validating monthly WFH quota
  //     return false;
  //   }
  // };

  const handleSubmitForTask = async () => {
    if (dataValidation()) {
      const bodyPayload = createTaskBodyJSON();

      // Monthly quota validation
      // const isQuotaValid = await isMonthlyQuotaValid(bodyPayload);
      // if (!isQuotaValid) {
      //   dipatchNotification(
      //     'error',
      //     'Monthly WfhHours quota exceeded',
      //   );
      //   return;
      // }
      if (validReqWfhHours(bodyPayload)) {
        setLoadingApiCall(true);
        const createTaskResponse = await addWFHTask(bodyPayload);
        await Promise.all(
          tasksToBeDeleted.map(async (id) => {
            await deleteWFHTask(id, currentUserId, wfh.id);
          }),
        );
        setLoadingApiCall(false);
        if (createTaskResponse.isError === false) {
          handleClose();
          setRefresh(!refresh);
        }
        dipatchNotification(
          createTaskResponse.type,
          createTaskResponse.message,
        );
        dispatch({
          type: RESET_DATE_RANGE,
        });
      } else {
        dipatchNotification(
          'error',
          'Daily WFH hours for a date cannot exceed 15 hours',
        );
      }
    }
  };

  // Soft UX check: batch total per date must not exceed 15 hours (54000 seconds).
  // Backend remains authoritative against existing DB tasks across submissions.
  const validReqWfhHours = (bodyPayload) => {
    const totalRequestedWfhHoursPerDate = {};
    const MAX_DAILY_WFH_SECONDS = 54000;

    for (const task of bodyPayload.wfhTaskDTOList) {
      const dateOfWfh = task.dateOfWfh;
      const requestedWfhHours = task.requestedWfhHours || 0;
      if (
        (totalRequestedWfhHoursPerDate[dateOfWfh] || 0) + requestedWfhHours >
        MAX_DAILY_WFH_SECONDS
      ) {
        return false;
      } else {
        totalRequestedWfhHoursPerDate[dateOfWfh] =
          (totalRequestedWfhHoursPerDate[dateOfWfh] || 0) + requestedWfhHours;
      }
    }
    return true;
  };

  const onBlurMulti = (e, index, timingsIndex) => {
    let value = e.target.value;
    const time = hourValidation(value, index, timingsIndex);
    let newTasks = [...multipleTasks];
    newTasks[index].timings[timingsIndex].requestedWfhHours =
      getHHMMSSFromSeconds(time);
    setMultipleTasks(newTasks);
  };

  const handleTimeError = (timeError, timeErrorMsg, index, timingsIndex) => {
    let newTasks = multipleTasks;
    newTasks[index].timings[timingsIndex].timeError = timeError;
    newTasks[index].timings[timingsIndex].timeErrorMsg = timeErrorMsg;
    setMultipleTasks(newTasks);
  };
  const handleAddTasktoDate = (event, index) => {
    setMultipleTasks((prevTasks) => [
      ...prevTasks.slice(0, index),
      {
        ...prevTasks[index],
        timings: [
          ...prevTasks[index].timings,
          {
            dateOfWfh: prevTasks[index].date,
            title: '',
            applicationId: wfh.id,
            description: '',
            projectId: '',
            taskType: '',
            approverId: '',
            officeHours: null,
            requestedWfhHours: null,
            approvedWfhHours: null,
            externalHours: 0,
            timeError: false,
            timeErrorMsg: '',
            titleError: false,
            descriptionError: false,
            projectError: false,
            approverIdError: false,
          },
        ],
      },
      ...prevTasks.slice(index + 1),
    ]);
    setApproverList((prevApproverList) => {
      const updatedList = [...prevApproverList];
      updatedList[index].push([]);
      return updatedList;
    });
  };
  const handleChangeTaskDescription = (event, index, timingsIndex) => {
    setMultipleTasks((prevTasks) =>
      prevTasks.map((task, i) => {
        if (i === index) {
          return {
            ...task,
            timings: task.timings.map((timing, j) => {
              if (j === timingsIndex) {
                return {
                  ...timing,
                  description: event.target.value,
                  descriptionError: false,
                };
              }
              return timing;
            }),
          };
        }
        return task;
      }),
    );
  };

  const handleRemoveTask = async (index, timingsIndex) => {
    const id = multipleTasks[index].timings[timingsIndex].id;
    const updatedTimings = [
      ...multipleTasks[index].timings.slice(0, timingsIndex),
      ...multipleTasks[index].timings.slice(timingsIndex + 1),
    ];
    const newTasks = [
      ...multipleTasks.slice(0, index),
      { ...multipleTasks[index], timings: updatedTimings },
      ...multipleTasks.slice(index + 1),
    ];
    setMultipleTasks(newTasks);
    if (editable && typeof id !== 'undefined') {
      setTasksToBeDeleted((prev) => [...prev, id]);
      dispatch({
        type: RESET_DATE_RANGE,
      });
    }
  };

  const getMultiTime = (index, timingsIndex) => {
    const date = multipleTasks[index].date;
    const newDateobj = new Date(date);
    newDateobj.setHours(0);
    newDateobj.setMinutes(0);
    newDateobj.setSeconds(0);
    const setTimings =
      multipleTasks[index].timings[timingsIndex].requestedWfhHours;

    if (setTimings !== null) {
      const timeArr = setTimings.split(':');
      let timeObj = new Date(
        newDateobj.getTime() +
          (parseInt(timeArr[0]) * 60 + parseInt(timeArr[1])) * 60000,
      );

      return timeObj;
    }

    return null;
  };
  const handleChangeForApprover = (event, index, timingsIndex) => {
    const approverId = event.target.value;
    setMultipleTasks(
      multipleTasks.map((task, i) => {
        if (i === index) {
          return {
            ...task,
            timings: task.timings.map((timing, j) => {
              if (j === timingsIndex) {
                return {
                  ...timing,
                  approverId: approverId,
                  approverIdError: approverId === '',
                };
              }
              return timing;
            }),
          };
        }
        return task;
      }),
    );
  };

  const handleChangeForWFOProject = async (event, index, timingsIndex) => {
    const projectId = event.target.value;
    setMultipleTasks(
      multipleTasks.map((task, i) => {
        if (i === index) {
          return {
            ...task,
            timings: task.timings.map((timing, j) => {
              if (j === timingsIndex) {
                return {
                  ...timing,
                  projectId: projectId,
                  projectError: projectId === '',
                  approverId: '',
                };
              }
              return timing;
            }),
          };
        }
        return task;
      }),
    );
    const list = await loadApproversForProject(projectId);
    setApproverList((prevApproverList) => {
      const updatedList = [...prevApproverList];
      updatedList[index][timingsIndex] = list;
      return updatedList;
    });
  };

  const handleChangeTaskTitle = (event, index, timingsIndex) => {
    setMultipleTasks(
      multipleTasks.map((task, i) => {
        if (i === index) {
          return {
            ...task,
            timings: task.timings.map((timing, j) => {
              if (j === timingsIndex) {
                return {
                  ...timing,
                  title: event.target.value,
                  titleError: false,
                };
              }
              return timing;
            }),
          };
        }
        return task;
      }),
    );
  };
  const hourValidation = (value, index, timingsIndex) => {
    let sec = 0;
    if (value === null) {
      sec = 0;
    } else {
      sec = Math.max(0, getSecondsFromHHMMSS(value));
    }
    const secInADay = 23 * 60 * 60 + 59 * 60;
    setFieldError((prev) => {
      return { ...prev, timeDuration: false };
    });
    setErrorMsg((prev) => {
      return {
        ...prev,
        timeDuration: '',
      };
    });
    let hours = 0;
    let minutes = 0;
    if (value === null) {
      hours = 0;
      minutes = 0;
    } else {
      const [inputHours, inputMinutes] = value.split(':');
      hours = parseInt(inputHours, 10);
      minutes = parseInt(inputMinutes, 10);
    }
    // Convert the hours and minutes to numbers

    if (sec > secInADay || sec <= 0 || minutes > 59) {
      const newErrorMessage = TaskFieldError.INVALID_DURATION;
      setTimeErrorMsg(newErrorMessage);

      if (
        index == undefined ||
        index == null ||
        timingsIndex == null ||
        timingsIndex == undefined
      ) {
        setFieldError((prev) => {
          return { ...prev, timeDuration: true };
        });
        setErrorMsg((prev) => {
          return {
            ...prev,
            timeDuration: `${TaskFieldError.INVALID_DURATION}`,
          };
        });
      } else {
        handleTimeError(true, newErrorMessage, index, timingsIndex);
      }
      return 0;
    } else {
      if (index == undefined || index == null) {
        setFieldError((prev) => {
          return { ...prev, timeDuration: false };
        });
        setErrorMsg((prev) => {
          return {
            ...prev,
            timeDuration: '',
          };
        });
      } else {
        handleTimeError(false, '', index, timingsIndex);
      }
      return sec;
    }
  };

  const setTimingsError = (multipleTasks, index, timingsIndex) => {
    let newTasks = [...multipleTasks];
    newTasks[index].timings[timingsIndex].timeError = false;
    newTasks[index].timings[timingsIndex].timeErrorMsg = '';
    setMultipleTasks(newTasks);
  };

  const createTaskBodyJSON = () => {
    let updatedTasks = multipleTasks;
    updatedTasks = multipleTasks.map((task) => {
      const newTimings = task.timings.filter(
        (timing) => timing.status !== APPROVED || timing.status === undefined,
      );
      const updatedTimings = newTimings.map((timing) => {
        const seconds = getSecondsFromHHMMSS(timing.requestedWfhHours);
        timing.requestedWfhHours = seconds;
        timing.taskType = WFO;
        timing.isUpdatedByHr = false;
        return timing;
      });
      task.timings = updatedTimings;
      return task;
    });
    const updatedTimings = updatedTasks
      .reduce((acc, task) => {
        return acc.concat(task.timings);
      }, [])
      .map((timing) => {
        const {
          titleError,
          descriptionError,
          projectError,
          timeError,
          timeErrorMsg,
          approverIdError,
          approverIdErrorMsg,
          ...rest
        } = timing;
        return rest;
      });
    const bodyJson = {
      employeeId: currentUserId,
      wfhTaskDTOList: updatedTimings,
    };
    return bodyJson;
  };

  return (
    <Container disableGutters sx={{ overflow: 'overlay' }}>
      <Paper component={Box} elevation={0} p={4}>
        <Grid container>
          <Grid item md={12} mt={-2} xs={12}>
            <IconButton
              sx={{ mt: '0px', ml: '-8px' }}
              onClick={handleCancelForm}
            >
              <KeyboardBackspaceIcon />
            </IconButton>
          </Grid>
        </Grid>
        <Box>
          {multipleTasks.map((items, index) => (
            <Box key={index}>
              {!isHolidayOrLeave(moment(items.date)) && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'row', md: 'row' },
                    marginBottom: { xs: 2, md: 2 },
                  }}
                >
                  <Typography
                    sx={{
                      marginRight: { xs: 0, md: 3 },
                      marginY: { xs: 2 },
                    }}
                    size="small"
                  >
                    {moment(items.date).format('DD/MM/YYYY')}
                  </Typography>
                  <Tooltip title="Add a task">
                    <IconButton
                      onClick={(event) => {
                        handleAddTasktoDate(event, index);
                      }}
                    >
                      <AddCircleIcon color="success" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
              {multipleTasks[index].timings.map((item, timingsIndex) =>
                moment(item.dateOfWfh).format('DD/MM/YYYY').toString() ===
                moment(items.date).format('DD/MM/YYYY').toString() ? (
                  <Box
                    sx={{ marginBottom: { xs: 3, md: 5 } }}
                    key={timingsIndex}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: '100%', md: '30%' },
                          marginRight: { md: 2 },
                          marginBottom: { xs: 2.3 },
                        }}
                      >
                        <FormControl
                          size="small"
                          fullWidth
                          error={
                            multipleTasks[index].timings[timingsIndex]
                              .projectError
                          }
                          disabled={isProjectAndApproverDisabled(
                            multipleTasks[index].timings[timingsIndex],
                          )}
                        >
                          <InputLabel id="taskProjectSelect">
                            {PROJECT}
                          </InputLabel>
                          <Select
                            name="project"
                            labelId="taskProjectSelect"
                            value={
                              multipleTasks[index].timings[timingsIndex]
                                .projectId || ''
                            }
                            label={PROJECT}
                            onChange={(event) => {
                              handleChangeForWFOProject(
                                event,
                                index,
                                timingsIndex,
                              );
                            }}
                          >
                            {userProjects.map((project) => (
                              <MenuItem
                                value={project.projectId}
                                key={project.id}
                              >
                                {project.projectName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                      <Box
                        sx={{
                          width: { xs: '100%', md: '30%' },
                          marginBottom: { xs: 2.3 },
                          marginRight: '6px',
                        }}
                      >
                        <FormControl
                          size="small"
                          fullWidth
                          error={
                            multipleTasks[index].timings[timingsIndex]
                              .approverIdError
                          }
                          disabled={isProjectAndApproverDisabled(
                            multipleTasks[index].timings[timingsIndex],
                          )}
                        >
                          <InputLabel id="taskApproverSelect">
                            {APPROVER}
                          </InputLabel>
                          <Select
                            name="approver"
                            labelId="taskApproverSelect"
                            value={
                              multipleTasks[index].timings[timingsIndex]
                                .approverId || ''
                            }
                            label={APPROVER}
                            onChange={(event) => {
                              handleChangeForApprover(
                                event,
                                index,
                                timingsIndex,
                              );
                            }}
                          >
                            {approverList[index]?.[timingsIndex]?.map(
                              (item) => (
                                <MenuItem value={item.employeeId} key={item.id}>
                                  {item.employeeName}
                                </MenuItem>
                              ),
                            )}
                          </Select>
                        </FormControl>
                      </Box>
                      {editable ? (
                        <Box display="flex" alignItems="center">
                          <Tooltip title="Remove">
                            <IconButton
                              onClick={() => {
                                handleRemoveTask(index, timingsIndex);
                              }}
                              disabled={
                                multipleTasks[index].timings[timingsIndex]
                                  .status == 'APPROVED'
                              }
                            >
                              <CloseIcon color="error" />
                            </IconButton>
                          </Tooltip>
                          <Typography variant="body2" color="textSecondary">
                            Delete Task
                          </Typography>
                        </Box>
                      ) : null}
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: '100%', md: '30%' },
                          marginRight: { md: 2 },
                          marginBottom: { xs: 2.3, md: 0 },
                        }}
                      >
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <TimePicker
                            PopperProps={{
                              sx: {
                                '& .css-1anqmj6-MuiPopper-root-MuiPickersPopper-root':
                                  {
                                    zIndex: 1300,
                                    display: 'flex !important',
                                    position: 'absolute !important',
                                  },
                              },
                            }}
                            ampm={false}
                            label="Time"
                            onViewChange={() =>
                              setTimingsError(
                                multipleTasks,
                                index,
                                timingsIndex,
                              )
                            }
                            value={getMultiTime(index, timingsIndex)}
                            onChange={(e) =>
                              handleChangeForRangeSelection(
                                e,
                                index,
                                timingsIndex,
                              )
                            }
                            renderInput={(params) => (
                              <TextField
                                FormHelperTextProps={{ sx: { marginTop: 0 } }}
                                {...params}
                                onBlur={(e) =>
                                  onBlurMulti(e, index, timingsIndex)
                                }
                                error={item.timeError}
                                helperText={item.timeErrorMsg}
                                size="small"
                              />
                            )}
                            disabled={
                              multipleTasks[index].timings[timingsIndex]
                                .status == 'APPROVED'
                            }
                            maxTime={dayjs().hour(12).minute(0)}
                          />
                        </LocalizationProvider>
                      </Box>
                      <Box
                        sx={{
                          marginBottom: { xs: 2.3 },
                          marginRight: { md: 2 },
                          width: { xs: '100%', md: '30%' },
                        }}
                      >
                        <TextField
                          name="taskTitle"
                          value={
                            multipleTasks[index].timings[timingsIndex].title ||
                            ''
                          }
                          onChange={(event) => {
                            handleChangeTaskTitle(event, index, timingsIndex);
                          }}
                          label={TASK_TILE}
                          size="small"
                          inputProps={{ maxLength: 50 }}
                          error={
                            multipleTasks[index].timings[timingsIndex]
                              .titleError
                          }
                          helperText={
                            multipleTasks[index].timings[timingsIndex]
                              .titleError
                              ? TaskFieldError.TITLE_MIN_NON_WHITESPACE
                              : ''
                          }
                          disabled={
                            multipleTasks[index].timings[timingsIndex].status ==
                            'APPROVED'
                          }
                        />
                      </Box>
                      <Box
                        sx={{
                          marginRight: { md: 2 },
                          width: { xs: '80%', md: '25%' },
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              onChange={() =>
                                handleOnChangeAppraisal(index, timingsIndex)
                              }
                              checked={
                                !!multipleTasks[index].timings[timingsIndex]
                                  .isAppraisal
                              }
                            />
                          }
                          label={APPRAISAL}
                          disabled={
                            multipleTasks[index].timings[timingsIndex].status ==
                            'APPROVED'
                          }
                        />
                      </Box>

                      {!editable &&
                      (multipleTasks[index].timings.length > 1 ||
                        isHolidayOrLeave(
                          `${new Date(multipleTasks[index].date).getDate().toString().padStart(2, '0')} ${'0' + (new Date(multipleTasks[index].date).getMonth() + 1).toString().slice(0, 2)} ${new Date(multipleTasks[index].date).getFullYear().toString()}`,
                        )) ? (
                        <Box>
                          <Tooltip title="Remove">
                            <IconButton
                              onClick={() => {
                                handleRemoveTask(index, timingsIndex);
                              }}
                            >
                              <CloseIcon color="error" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : null}
                    </Box>
                    <Box>
                      <TextField
                        name="description"
                        value={
                          multipleTasks[index].timings[timingsIndex]
                            .description || ''
                        }
                        label={TASK_DESCRIPTION}
                        multiline
                        rows={2}
                        size="small"
                        inputProps={{ maxLength: 300 }}
                        onChange={(event) =>
                          handleChangeTaskDescription(
                            event,
                            index,
                            timingsIndex,
                          )
                        }
                        disabled={
                          multipleTasks[index].timings[timingsIndex].status ==
                          'APPROVED'
                        }
                        error={
                          multipleTasks[index].timings[timingsIndex]
                            .descriptionError
                        }
                        helperText={
                          multipleTasks[index].timings[timingsIndex]
                            .descriptionError
                            ? TaskFieldError.DESCRIPTION_MIN_NON_WHITESPACE
                            : ''
                        }
                      />
                    </Box>
                  </Box>
                ) : null,
              )}
            </Box>
          ))}
        </Box>
        <Grid container className="space-between-container">
          <Grid items="true">
            <Button
              onClick={handleCancelForm}
              variant="outlined"
              color={ERROR_KEY}
            >
              {CANCEL_BUTTON_TEXT}
            </Button>
          </Grid>

          <Grid
            items="true"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
            }}
          >
            <LoadingButton
              onClick={() => handleSubmitForTask()}
              loading={loadingApiCall}
              variant="contained"
            >
              {loadingApiCall ? 'loading...' : SUBMIT_BUTTON_TEXT}
            </LoadingButton>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default WFHTask;
